import { Dispatch, Middleware } from '@reduxjs/toolkit';
import { authManager, noTokenRedirect } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';

import { appActions } from 'app/app.actions';
import { AppDispatch, AppState } from 'bootstrap.store';
import { getRequestID } from 'components/shared/util/request';
import { debugLog } from 'utils/debugLog';
import { pubSub } from 'utils/pubSub';

import { twilioActions } from '../../../app/middleware/twilio/actions/twilioActions';
import { actions as conversationActions } from '../../../components/conversation/actions/conversation';
import {
  errorReconnectAttempt,
  errorReconnectAttemptReset,
} from '../../../components/errors/reducers/errors';
import { notificationActions } from '../../../components/notifications/actions/notification';
import { presenceActions } from '../../../components/presence/actions/presenceActions';

import {
  websocketActions,
  WebsocketCreateAction,
  WebsocketRequestAction,
} from './actions/websocket';
import { responseDeffers } from './responseDeffers';
import {
  getSocketOrWorker,
  SharedWorkerImplementation,
} from './socketInterface';
import { websocketSubscriptions } from './subscriptions';
import {
  WebsocketRequestMessage,
  WebsocketSubscriptionMessage,
} from './types/outboundWebsocketEvent';

const { twilioClose } = twilioActions;

const { notificationFaviconChange } = notificationActions;
const { presenceShowOffline } = presenceActions;

const { conversationGet } = conversationActions;

export const websocketMiddleware =
  (): Middleware<void, AppState, Dispatch> => (store) => {
    const dispatch: AppDispatch = store.dispatch;
    return (next: AppDispatch) => (action) => {
      try {
        let die = false;

        const init = async (createAction: WebsocketCreateAction) => {
          const socketOrWorker = await getSocketOrWorker();

          try {
            await socketOrWorker.start();

            socketOrWorker.onMessage((data) => {
              if ('type' in data) {
                switch (data.type) {
                  case 'auth_failure': {
                    // App token is invalid.
                    socketOrWorker.close();
                    die = true;
                    next(notificationFaviconChange({ type: 'offline' })); // Update favicon.

                    Sentry.captureMessage('websocket auth failure', {
                      level: 'debug',
                      tags: {
                        team: SentryTeam.Platform,
                      },
                    });
                    console.error('Websocket auth failure');
                    authManager.removeAuthData();

                    ampli.websocketConnectionClosed({
                      source: 'auth_failure_message',
                    });

                    next(twilioClose(data));
                    next(presenceShowOffline());
                    // Redirect the browser to login / signin page.
                    noTokenRedirect();
                    break;
                  }
                  case 'connection:active_connection_changed': {
                    // Connection active changed modal show.
                    // We got a connection changed. Telling us that the api
                    // has detected a new connection and closes this websocket connection.
                    socketOrWorker.close();
                    die = true;

                    ampli.websocketConnectionClosed({
                      source: 'active_connection_changed_message',
                    });

                    const currentState = store.getState();

                    dispatch(
                      appActions.showModal({
                        modal: { type: 'app:webSocketConnectionChanged' },
                      }),
                    );

                    const oldConnectionId =
                      currentState.websocket.connectionId ?? '';
                    const newConnectionId = data.params.new_connection_id;

                    debugLog('WEBSOCKET-MANAGER', 'Active connection changed', {
                      oldConnectionId,
                      newConnectionId,
                    });
                    ampli.websocketActiveConnectionChanged({
                      oldConnectionId,
                      newConnectionId,
                    });

                    next(presenceShowOffline());
                    next(notificationFaviconChange({ type: 'offline' })); // Update favicon.
                    break;
                  }
                  case 'hello_agent': {
                    const dataCopy = {
                      ...data,
                      init: createAction.payload?.init === true,
                    };

                    createAction.meta.resolve(dataCopy); // Hello received, we resolve the promise.

                    next(websocketActions.createFulfilled(dataCopy));
                    next(errorReconnectAttemptReset());
                    next(websocketActions.cancelReconnect());
                    next(
                      appActions.hideModal({
                        type: 'app:networkConnectionLost',
                      }),
                    );

                    const currentState = store.getState();
                    let currentConversationId: undefined | number | null = null;
                    if (
                      currentState.conversation.metaConversation &&
                      currentState.conversation.metaConversation.conversation
                    ) {
                      currentConversationId =
                        currentState.conversation.metaConversation.conversation
                          .id;
                    }

                    // Resubscribe to all previous subscriptions.
                    for (const subscription of websocketSubscriptions.getAll()) {
                      if (
                        subscription.params.type === 'conversation' &&
                        subscription.params.csid === currentConversationId
                      ) {
                        // Reload only the conversation the user is currently viewing. This will
                        // also resubscribe to events for this conversation through the saga, so
                        // it's not necessary to resubscribe here to prevent duplicate requests.  .
                        next(
                          conversationGet({ csid: subscription.params.csid }),
                        );
                      } else {
                        // Reload the dashboard queues and also resubscribe to events.
                        if (
                          subscription.params.type === 'real_time_dashboard'
                        ) {
                          pubSub.publish('real_time_dashboard:refetch');
                        }

                        // Resubscribing to all previous subscriptions. Because this action is handled
                        // by this middleware, using `next` will only pass the action to the next
                        // middleware and prevent it from reaching this middleware again. Instead, we
                        // use `store.dispatch`, which triggers a new dispatch cycle and ensures that
                        // the action is handled by all middlewares, including this one.
                        dispatch(websocketActions.request(subscription));
                      }
                    }

                    debugLog('WEBSOCKET-MANAGER', 'Connection established');
                    break;
                  }
                  default:
                    // stop worker messages from being sent to the websocket
                    if (data.type.startsWith('worker:')) return;
                    next(websocketActions.eventReceived(data));
                    break;
                }
              } else if ('ping' in data && data.ping != null) {
                // Response to the ping request.
                socketOrWorker.send({
                  pong: data.ping,
                });
              } else if ('error' in data && data.error != null) {
                // data.reply_to to null if response was to a request.
                // So we do not handle it. We just dispatch a WS error.
                next(websocketActions.errorReceived(data.error));
              } else if ('reply_to' in data && data.reply_to != null) {
                // Ws response received.
                const { reply_to, error } = data;
                responseDeffers.handleResponse(reply_to as string, data, error);
                next(websocketActions.replyReceived(data));
              } else {
                console.info('WS: unknown ws event received', data);
              }
            });

            socketOrWorker.onError((error) => {
              debugLog('WEBSOCKET-MANAGER', 'Failed to connect');
              createAction.meta.reject(error);
              next(websocketActions.createRejected(error));
            });

            if (socketOrWorker instanceof SharedWorkerImplementation) {
              socketOrWorker.onPrimaryConnectionChange((isPrimary) => {
                next(websocketActions.connectionPrimary(isPrimary));
              });
            }

            socketOrWorker.onClose(() => {
              // Dispatch the websocketActions.createRejected action
              // to avoid any state where the create WS still is pending,
              // but a close event is intervening before the request is fulfilled.
              next(websocketActions.createRejected());

              if (!die) {
                debugLog('WEBSOCKET-MANAGER', 'Disconnected');
                next(websocketActions.closeReceived());
              }
            });
          } catch (exception) {
            console.warn('WS: init exception', exception);
            createAction.meta.reject(exception);
            next(websocketActions.createRejected());
          }
        };

        const handleRequestAction = async (
          payload: WebsocketRequestMessage,
          meta: WebsocketRequestAction['meta'],
        ) => {
          const socketOrWorker = await getSocketOrWorker();

          // Send message via websockets.
          if (socketOrWorker.isConnected) {
            responseDeffers.handleRequest(payload.id as string, meta);

            socketOrWorker.send(payload);
          } else {
            // Maybe here we should add our queue ?
            meta.reject('WS: ws connection is closed');
          }
        };

        const disconnect = async () => {
          const socketOrWorker = await getSocketOrWorker();
          socketOrWorker.close();
        };

        switch (action.type) {
          case websocketActions.request.type: {
            const { payload, meta } = action as WebsocketRequestAction;

            if (payload.id === undefined) {
              payload.id = getRequestID();
            }

            websocketSubscriptions.handleRequest(
              payload as WebsocketSubscriptionMessage,
            );

            handleRequestAction(payload, meta);

            break;
          }
          case websocketActions.create.type: {
            debugLog(
              'WEBSOCKET-MANAGER',
              'Create action received, connecting...',
            );

            authManager
              .getAuthData()
              .then(() => init(action as WebsocketCreateAction));
            break;
          }
          case websocketActions.close.type: {
            debugLog(
              'WEBSOCKET-MANAGER',
              'Close action received, disconnecting...',
            );
            ampli.websocketConnectionClosed({
              source: 'close_action',
            });

            disconnect();
            break;
          }
          case errorReconnectAttempt.toString(): {
            if (die === false) {
              next(websocketActions.reconnectAttempt());
            } else {
              debugLog('WEBSOCKET-MANAGER', 'Reconnect aborted, die is true');
            }
            break;
          }
        }
      } catch (exception) {
        Sentry.captureException(
          new Error('Websocket: middleware exception caught'),
          { extra: { exception } },
        );
      }

      return next(action);
    };
  };
